import React, { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { Pencil, Settings2, Sprout, X } from "lucide-react"
import { getBackendUrl } from '../env'
import { useAuthContext } from '../hooks/useAuthContext'
import AutomaticProgram from './AutomaticProgram'
import ManualProgram from "./ManualProgram";
import Select, { type MultiValue, type StylesConfig } from "react-select";
import { hydroReactSelectStyles } from "@/lib/hydroReactSelectStyles";
import { getDevicesWithoutSystem, removeSystemFromDevice, removeDeviceFromSystem } from "../helper/deviceHelper";
import { ManualControls, System, SystemDevice } from "../context/SystemContext";
import { Program } from "../types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AUTH_INPUT_CLASS, AUTH_TEXTAREA_CLASS } from "@/components/auth/AuthPageShell"
import { cn } from "@/lib/utils"
interface SystemFormProps {
    system: System;
    setSystem: React.Dispatch<React.SetStateAction<System>>;
    systemPopup: boolean;
    setSystemPopup: React.Dispatch<React.SetStateAction<boolean>>;
    newSystem: boolean;
    setNewSystem: React.Dispatch<React.SetStateAction<boolean>>;
  }

const clearedSettings = [
    {name: 'System Name', value: '', type:'Text', collapsible:false},
    {name: 'Reservoir Size', value: '', type:'Number', collapsible:false},
    {name: 'Description', value: '', type:'Text', collapsible:false},
    {name: 'Devices', value: 0, type:'MultiSelect', collapsible:false},
    {name: 'Program Type', value: 'Manual', type:'Slider', collapsible:true},
]
export type editingSensorGroups = {
    ec_device: boolean;
    ph_device: boolean;
    temperature_device: boolean;
    humidity_device: boolean;
    moisture_device: boolean;
    ec_sensor: boolean;
    ph_sensor: boolean;
    temperature_sensor: boolean;
    humidity_sensor: boolean;
    moisture_sensor: boolean;
    pump_device: boolean;
    light_device: boolean;
    water_flow_sensor: boolean;
    water_flow_device: boolean;
}
const deviceSelectStyles = hydroReactSelectStyles as StylesConfig<SystemDevice, true>

const fieldToHandlerMap = {
    'System Name': 'name',
    'Description': 'description',
    'Reservoir Size': 'reservoir',
    'Devices': 'devices',
    'Program Type': 'program',
};

const defaultSensorGroup:any = {
    ec_device: false,
    ph_device: false,
    temperature_device: false,
    humidity_device: false,
    moisture_device: false,
    ec_sensor: false,
    ph_sensor: false,
    temperature_sensor: false,
    humidity_sensor: false,
    moisture_sensor: false,
    pump_device: false,
    light_device: false,
    water_flow_sensor: false,
    water_flow_device: false,
}

const SystemForm = ({system, setSystem, systemPopup, setSystemPopup, newSystem, setNewSystem}: SystemFormProps) => {
    //Reducers
    const { user }  = useAuthContext()
    //States
    const [devicesToSelect, setDevicesToSelect] = useState([])
    const [error, setError] = useState<string | null>(null)
    const [devices, setDevices] = useState<SystemDevice[]>([])
    const [selected, setSelected] = useState<SystemDevice[]>([])
    const [manualControls, setManualControls] = useState<ManualControls[]>([])
    //Values for the system form
    const [systemName, setSystemName] = useState<string>('')
    const [reservoirSize, setReservoirSize] = useState<number>()
    const [description, setDescription] = useState<string>('')
    const [sensorGroup, setSensorGroup] = useState<any[]>([])
    const [systemId, setSystemId] = useState<string>('')
    const [programType, setProgramType] = useState<string>('Manual')
    const [program, setProgram] = useState<Program>({
        _id:'',
        name:'',
        temperature: 0,
        humidity: 0,
        ec: 0,
        ph: 0,
        lights_on: 0
    })
    const [rows, setRows] = useState<any[]>(clearedSettings)
    const [defaultOptions, setDefaultOptions] = useState<boolean>(true)
    const [advancedOptions, setAdvancedOptions] = useState<object>({})
    //Editing Fields, Program, and Sensor Groups
    const [editingName, setEditingName] = useState<boolean>(false)
    const [changingReservoirSize, setChangingReservoirSize] = useState<boolean>(false)
    const [editingDescription, setEditingDescription] = useState<boolean>(false)
    const [editingSystemDevices, setEditingSystemDevices] = useState<boolean>(false)
    const [changingProgramType, setChangingProgramType] = useState<boolean>(false)
    const [changingProgram, setChangingProgram] = useState<boolean>(false)
    const [editingSensorGroups, setEditingSensorGroups] = useState<editingSensorGroups>(defaultSensorGroup)

    const removeDeviceSystem = async (device: SystemDevice) =>{
        if(user){
            const response:any = await removeDeviceFromSystem(user, system, device)
            const json = await response.json()
            if(response.ok){
                const data = await removeSystemFromDevice(user, device._id, json)
                setDevices(data.devices)
                setSystem(data.system)                
            }
        }
    }

    const handleEdit = async(e:string) =>{
        if (e === 'name'){
            setEditingName(!editingName)
        }
        if(e === 'description'){
            setEditingDescription(!editingDescription)
        }
        if(e === 'devices'){
            requestDevicesWithoutSystem()
            setEditingSystemDevices(!editingSystemDevices)
        }
        if(e === 'program'){
            setChangingProgramType(!changingProgramType)
        }
        if(e === 'reservoir'){
            setChangingReservoirSize(!changingReservoirSize)
        }
    }  

    const requestDevicesWithoutSystem = async () => {
        if (user) {
            const json = await getDevicesWithoutSystem(user)
            setDevicesToSelect(json)
        }
    }

    const createSystem = (selectedDevices: { _id: string; device_id: string; name: string; device_type: string; }[]) =>{
        let data: any = {
            devices: editingSystemDevices && (system instanceof Object && '_id' in system) ? [...system.devices, ...selectedDevices] : (system instanceof Object && '_id' in system)? [...system.devices] : [],
            name: editingName ? systemName : undefined,
            description: editingDescription ? description : undefined,
            programType: changingProgramType ? programType : undefined,
            reservoir_size: changingReservoirSize ? reservoirSize : undefined,
            program: changingProgram && programType === 'Automatic' && program !== null ? program : undefined,
            manualControls: manualControls !== null ? manualControls : undefined,
            default_options: defaultOptions,
            advanced_options: advancedOptions,
            sensor_groups: sensorGroup
        };
    
        // Remove undefined values
        data = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== undefined));
    
        return data;
    }

    const handleSave = async (val:boolean)  => {
        const rows = (json:any) => {
            return [
                {name:'System Name', value: json.name, type:'Text', collapsible:false},
                {name: 'Reservoir Size', value: json.reservoir_size, type:'Number', collapsible:false},
                {name: 'Description', value: json.description, type:'Text', collapsible:false},
                {name: 'Devices', value: json?.devices?.length, type:'MultiSelect', collapsible:false},
                {name: 'Program Type', value: json.programType, type:'Slider', collapsible:true},
            ]
        }
        const handleResponse = (response:any, json:any) =>{
            if(response.ok){
                setSystem(json)
                setDevices(json.devices)
                setSensorGroup(json.sensor_groups)
                setEditingDescription(false)
                setEditingSystemDevices(false)
                setChangingReservoirSize(false)
                setEditingSensorGroups(defaultSensorGroup)
                setEditingName(false)
                setSystemId(json._id)
                setSelected([])
                setRows(rows(json))
            }
            else{
                setError(json.error)
            }
        }

        if(user){
            const selectedDevices: SystemDevice[] = [];
            if(selected){
                selected.forEach((device) => selectedDevices.push({ '_id': device._id, 'device_id': device.device_id, name: device.name, device_type: device.device_type }))   
            }
            if (newSystem){
                const response = await fetch(`${getBackendUrl()}/api/systems/`, {
                    method: 'POST',
                    body: JSON.stringify({
                        "user_id": user.user_id, name: systemName, description: description,
                        devices: selectedDevices, programType: programType,
                        manualControls: manualControls, program:program, reservoir_size: reservoirSize,
                        default_options:defaultOptions, advanced_options: advancedOptions, sensor_group: sensorGroup
                    }),
                    headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                    }
                })
                
                const json = await response.json()
                handleResponse(response, json)
            }
            else{
                if( (system instanceof Object && '_id' in system)){
                    const response = await fetch(`${getBackendUrl()}/api/systems/`+system._id, {
                    method: 'PATCH',
                    body: JSON.stringify(createSystem(selectedDevices)),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                        }   
                    })
                    const json = await response.json()
                    handleResponse(response, json)
                }
            }
            if(val && error === null){
                closeWindow()
            }
        }    
    }

    const closeWindow = () => {
        setSystemPopup(false)
        setSelected([])
        setNewSystem(false)
        setEditingDescription(false)
        setEditingSystemDevices(false)
        setChangingProgram(false)
        setChangingProgramType(false)
        setManualControls([])
        setEditingName(false)
        setDevices([])
        setDevicesToSelect([])
        setSystem({})
        setDefaultOptions(true)
        setAdvancedOptions({})
        setSensorGroup([])
        setSystemName('')
        setRows(clearedSettings)
        setError(null)
      }

    useEffect(() => {
        if (!systemPopup) return
        const prev = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = prev
        }
    }, [systemPopup])

    useEffect(() => {
        if (!systemPopup) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeWindow()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [systemPopup])

    useEffect(()=>{
        let systemRowDetails: any = []
        if(systemPopup===true){
            if(!newSystem && (system instanceof Object && '_id' in system)){
                setDevices(system.devices)
                setSystemName(system.name)
                system.description?  setDescription(system.description): setDescription('');
                setReservoirSize(system.reservoir_size)
                setDefaultOptions(system.default_options)
                setAdvancedOptions(system.advanced_options)
                setSensorGroup(system.sensor_groups)
                setSelected(system.devices)
                setProgramType(system.programType)
                systemRowDetails = [
                    {name:'System Name', value: system.name, type:'Text', collapsible:false},
                    {name: 'Reservoir Size', value: system.reservoir_size, type:'Number', collapsible:false},
                    {name: 'Description', value: system.description, type:'Text', collapsible:false},
                    {name: 'Devices', value: system.devices?.length ?? 0, type:'MultiSelect', collapsible:false},
                    {name: 'Program Type', value: system.programType, type:'Slider', collapsible:true},
                ]
                setRows(systemRowDetails)
                if(system.program !== undefined) setProgram(system.program);
                if(typeof(system.program) === 'object') setProgram(program);
            }
            requestDevicesWithoutSystem()
        }
        
    },[systemPopup])

    const generateEdit = (fieldName: string) => {
        const handler = fieldToHandlerMap[fieldName as keyof typeof fieldToHandlerMap]
        if (!handler) return null
        return (
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-800"
                onClick={() => void handleEdit(handler)}
            >
                <Pencil className="mr-1.5 size-3.5 opacity-80" aria-hidden />
                Edit
            </Button>
        )
    }

    const generateInput = (fieldName: string, value: string | number | undefined) => {
        switch (fieldName) {
            case "System Name":
                return editingName ? (
                    <Input
                        required
                        autoComplete="off"
                        value={systemName}
                        onChange={(e) => setSystemName(e.target.value)}
                        className={cn(AUTH_INPUT_CLASS, "max-w-md")}
                    />
                ) : (
                    <span className="text-zinc-100">{value ?? "—"}</span>
                )
            case "Description":
                return editingDescription ? (
                    <textarea
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className={AUTH_TEXTAREA_CLASS}
                    />
                ) : (
                    <span className="whitespace-pre-wrap text-zinc-200">{value ? String(value) : "—"}</span>
                )
            case "Reservoir Size":
                return changingReservoirSize ? (
                    <Input
                        type="number"
                        min={0}
                        step="any"
                        value={reservoirSize === undefined || reservoirSize === null ? "" : reservoirSize}
                        onChange={(e) => {
                            const v = e.target.value
                            setReservoirSize(v === "" ? undefined : Number(v))
                        }}
                        className={cn(AUTH_INPUT_CLASS, "max-w-[12rem]")}
                    />
                ) : (
                    <span className="text-zinc-100">{value === undefined || value === null || value === "" ? "—" : String(value)}</span>
                )
            case "Devices":
                return editingSystemDevices ? (
                    <Select<SystemDevice, true>
                        instanceId="edit-system-devices"
                        inputId="edit-system-devices-input"
                        isMulti
                        isSearchable
                        classNamePrefix="hydro-devices"
                        options={devicesToSelect as SystemDevice[]}
                        value={selected}
                        onChange={(v: MultiValue<SystemDevice>) => setSelected(v ? Array.from(v) : [])}
                        getOptionLabel={(d) => d.name}
                        getOptionValue={(d) => d._id}
                        placeholder="Search devices to add…"
                        styles={deviceSelectStyles}
                        menuPlacement="auto"
                        captureMenuScroll={false}
                        menuShouldScrollIntoView
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        blurInputOnSelect={false}
                    />
                ) : (
                    <span className="text-zinc-100">
                        {typeof value === "number" ? `${value} device(s) assigned` : "—"}
                    </span>
                )

            case "Program Type":
                return changingProgramType ? (
                    <div
                        className={cn(
                            "inline-flex w-full max-w-md rounded-lg border border-zinc-600 bg-zinc-950 p-1 sm:w-auto"
                        )}
                        role="group"
                        aria-label="Program type"
                    >
                        <Button
                            type="button"
                            variant={programType === "Manual" ? "default" : "ghost"}
                            size="sm"
                            className={cn(
                                "flex-1 rounded-md sm:flex-none",
                                programType === "Manual" && "bg-emerald-600 text-white hover:bg-emerald-500"
                            )}
                            onClick={() => setProgramType("Manual")}
                        >
                            Manual
                        </Button>
                        <Button
                            type="button"
                            variant={programType === "Automatic" ? "default" : "ghost"}
                            size="sm"
                            className={cn(
                                "flex-1 rounded-md sm:flex-none",
                                programType === "Automatic" && "bg-emerald-600 text-white hover:bg-emerald-500"
                            )}
                            onClick={() => setProgramType("Automatic")}
                        >
                            Automatic
                        </Button>
                    </div>
                ) : (
                    <span className="text-zinc-100">{value ?? "—"}</span>
                )
            default:
                return null
        }
    }

    return systemPopup
        ? createPortal(
              <div
                  className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
                  role="presentation"
              >
                  <button
                      type="button"
                      className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                      aria-label="Close dialog"
                      onClick={closeWindow}
                  />
                  <div
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="system-form-title"
                      className={cn(
                          "relative z-10 flex max-h-[min(92dvh,880px)] w-full flex-col overflow-hidden rounded-t-2xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-2xl sm:max-w-2xl sm:rounded-2xl",
                          !newSystem && "sm:max-w-4xl"
                      )}
                      onClick={(e) => e.stopPropagation()}
                  >
                      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-zinc-800 px-4 py-4 sm:px-5">
                          <div className="flex min-w-0 items-center gap-3">
                              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/35">
                                  {newSystem ? (
                                      <Sprout className="size-5" aria-hidden />
                                  ) : (
                                      <Settings2 className="size-5" aria-hidden />
                                  )}
                              </div>
                              <div className="min-w-0">
                                  <h2
                                      id="system-form-title"
                                      className="truncate text-lg font-semibold tracking-tight text-zinc-50"
                                  >
                                      {newSystem
                                          ? "Create system"
                                          : system instanceof Object && "_id" in system
                                            ? `${system.name} · settings`
                                            : "System"}
                                  </h2>
                                  <p className="mt-0.5 text-sm leading-snug text-zinc-400">
                                      {newSystem
                                          ? "Name your grow, add optional details, and attach unassigned devices."
                                          : "Update configuration for this system."}
                                  </p>
                              </div>
                          </div>
                          <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                              onClick={closeWindow}
                              aria-label="Close"
                          >
                              <X className="size-5" />
                          </Button>
                      </div>

                      <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-5">
                          {error ? (
                              <div
                                  role="alert"
                                  className="mb-4 rounded-md border border-red-400/60 bg-red-950/80 px-4 py-3 text-sm leading-relaxed text-red-50"
                              >
                                  {error}
                              </div>
                          ) : null}
                          {newSystem ? (
                              <div className="flex w-full min-w-0 flex-col gap-5 pb-20 sm:pb-24">
                                  <div className="flex w-full min-w-0 flex-col gap-2">
                                      <Label htmlFor="new-system-name" className="text-zinc-200">
                                          System name <span className="text-red-400">*</span>
                                      </Label>
                                      <Input
                                          id="new-system-name"
                                          required
                                          autoComplete="off"
                                          placeholder="e.g. Tent A · lettuce"
                                          value={systemName}
                                          onChange={(e) => setSystemName(e.target.value)}
                                          className={cn(AUTH_INPUT_CLASS, "w-full")}
                                      />
                                  </div>
                                  <div className="flex w-full min-w-0 flex-col gap-2">
                                      <Label htmlFor="new-system-reservoir" className="text-zinc-200">
                                          Reservoir size (L)
                                      </Label>
                                      <Input
                                          id="new-system-reservoir"
                                          type="number"
                                          min={0}
                                          step="any"
                                          placeholder="Optional"
                                          value={reservoirSize === undefined || reservoirSize === null ? "" : reservoirSize}
                                          onChange={(e) => {
                                              const v = e.target.value
                                              setReservoirSize(v === "" ? undefined : Number(v))
                                          }}
                                          className={cn(AUTH_INPUT_CLASS, "w-full")}
                                      />
                                  </div>
                                  <div className="flex w-full min-w-0 flex-col gap-2">
                                      <Label htmlFor="new-system-description" className="text-zinc-200">
                                          Description
                                      </Label>
                                      <textarea
                                          id="new-system-description"
                                          rows={4}
                                          placeholder="Crop, notes for collaborators, etc."
                                          value={description}
                                          onChange={(e) => setDescription(e.target.value)}
                                          className={AUTH_TEXTAREA_CLASS}
                                      />
                                  </div>
                                  <div className="relative z-20 flex w-full min-w-0 flex-col gap-2 overflow-visible pb-1">
                                      <Label htmlFor="create-system-devices-input" className="text-zinc-200">
                                          Devices
                                      </Label>
                                      <p className="text-xs leading-snug text-zinc-500">
                                          Only devices not already on another system appear here.
                                      </p>
                                      <Select<SystemDevice, true>
                                          instanceId="create-system-devices"
                                          inputId="create-system-devices-input"
                                          isMulti
                                          isSearchable
                                          classNamePrefix="hydro-devices"
                                          options={devicesToSelect as SystemDevice[]}
                                          value={selected}
                                          onChange={(v: MultiValue<SystemDevice>) =>
                                              setSelected(v ? Array.from(v) : [])
                                          }
                                          getOptionLabel={(d) => d.name}
                                          getOptionValue={(d) => d._id}
                                          placeholder="Search devices to add…"
                                          styles={deviceSelectStyles}
                                          menuPlacement="auto"
                                          captureMenuScroll={false}
                                          menuShouldScrollIntoView
                                          closeMenuOnSelect={false}
                                          hideSelectedOptions={false}
                                          blurInputOnSelect={false}
                                      />
                                  </div>
                              </div>
                          ) : (
                              <div className="flex w-full min-w-0 flex-col gap-4 pb-20 sm:pb-24">
                                  {rows.map((row: { name: string; value: string | number | undefined }) => (
                                      <div
                                          key={row.name}
                                          className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5"
                                      >
                                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                                              <div className="min-w-0 flex-1 space-y-2">
                                                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                                      {row.name}
                                                  </p>
                                                  <div className="min-w-0">{generateInput(row.name, row.value)}</div>
                                              </div>
                                              <div className="flex shrink-0 justify-start sm:justify-end sm:pt-5">
                                                  {generateEdit(row.name)}
                                              </div>
                                          </div>
                                      </div>
                                  ))}

                                  {programType === "Manual" ? (
                                      <ManualProgram
                                          manualControls={manualControls}
                                          setManualControls={setManualControls}
                                          programType={programType}
                                          environment={system}
                                      />
                                  ) : (
                                      <AutomaticProgram
                                          sensorGroup={sensorGroup}
                                          setSensorGroup={setSensorGroup}
                                          setProgram={setProgram}
                                          programType={programType}
                                          system={system}
                                          changingProgram={changingProgram}
                                          setChangingProgram={setChangingProgram}
                                          program={program}
                                          defaultOptions={defaultOptions}
                                          advancedOptions={advancedOptions}
                                          setDefaultOptions={setDefaultOptions}
                                          setAdvancedOptions={setAdvancedOptions}
                                          setEditingSensorGroups={setEditingSensorGroups}
                                          editingSensorGroups={editingSensorGroups}
                                      />
                                  )}
                              </div>
                          )}
                      </div>

                      <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-zinc-800 px-4 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-5">
                          <Button
                              type="button"
                              variant="outline"
                              onClick={closeWindow}
                              className="border-zinc-600 bg-zinc-950 text-zinc-100 hover:bg-zinc-800"
                          >
                              Cancel
                          </Button>
                          <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                  void handleSave(false)
                              }}
                              className="border-zinc-600 bg-zinc-950 text-zinc-100 hover:bg-zinc-800"
                          >
                              Save
                          </Button>
                          <Button
                              type="button"
                              onClick={() => {
                                  void handleSave(true)
                              }}
                              className="bg-emerald-600 font-semibold text-white hover:bg-emerald-500"
                          >
                              Save and close
                          </Button>
                      </div>
                  </div>
              </div>,
              document.body
          )
        : null
}
 
export default SystemForm;